import { useState } from "react";
import { spotlights, type Person } from "@/mocks/homeData";

const changeStyle: Record<Person["changeType"], string> = {
  rise: "bg-accent-100 text-accent-800 ring-accent-200",
  stop: "bg-secondary-100 text-secondary-900 ring-secondary-200",
  pivot: "bg-primary-100 text-primary-700 ring-primary-200",
};
const changeIcon: Record<Person["changeType"], string> = {
  rise: "ri-arrow-up-line",
  stop: "ri-pause-circle-line",
  pivot: "ri-restart-line",
};
const changeLabel: Record<Person["changeType"], string> = {
  rise: "正在涨粉",
  stop: "停更观察",
  pivot: "正在转型",
};

export default function SpotlightSection() {
  const [following, setFollowing] = useState<string[]>([]);
  const toggle = (id: string) =>
    setFollowing((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  return (
    <section id="spotlight" className="bg-background-100/70">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-user-follow-line" />
              Who's On The Radar
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              本周焦点人物
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-foreground-700">
              停更、转型还是悄悄涨粉？这些人正在影响话题走向，点"追更"可加入你的关注清单。
            </p>
          </div>
          <p className="text-xs text-foreground-500">
            已追更 <span className="font-semibold text-primary-600">{following.length}</span> 人
          </p>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {spotlights.map((p) => {
            const isFollow = following.includes(p.id);
            return (
              <article
                key={p.id}
                className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5 transition-all hover:border-primary-200"
              >
                <div className="flex items-start gap-3">
                  <div className="relative shrink-0">
                    <div className="h-16 w-16 overflow-hidden rounded-2xl">
                      <img src={p.avatar} alt={`焦点人物：${p.name}`} className="h-full w-full object-cover object-top" />
                    </div>
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-background-50 ring-2 ring-background-50">
                      <i className="ri-award-line text-xs" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{p.name}</h3>
                    <p className="truncate text-xs text-foreground-500">{p.handle}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="rounded-md bg-background-200 px-2 py-0.5 text-foreground-700">{p.category}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600">
                        <i className="ri-user-follow-line" />
                        {p.followers}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    aria-pressed={isFollow}
                    className={`inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs font-semibold transition-colors ${
                      isFollow
                        ? "border border-background-300 bg-background-100 text-foreground-700"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={isFollow ? "ri-check-line" : "ri-add-line"} />
                    {isFollow ? "已追更" : "追更"}
                  </button>
                </div>

                <span
                  className={`mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-xs font-semibold ring-1 ${changeStyle[p.changeType]}`}
                >
                  <i className={changeIcon[p.changeType]} />
                  {changeLabel[p.changeType]}
                </span>
                <p className="mt-2 text-sm font-medium leading-snug text-foreground-900">{p.change}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.blurb}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}